import type {
  OutgoingIntake,
  OutgoingIntakeSubmission
} from "../../../api/types/outgoingIntake";

export type IntakeTimelineEventKind =
  | "sent"
  | "opened"
  | "submitted"
  | "revision_requested";

export type IntakeTimelineEvent = {
  id: string;
  kind: IntakeTimelineEventKind;
  label: string;
  at: unknown;
  actor: "attorney" | "client";
};

function safeString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function toMillis(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  try {
    let d: Date | null = null;
    if (typeof value === "string") {
      const s = value.trim();
      if (!s) return null;
      d = new Date(s);
    } else if (typeof value === "number") d = new Date(value);
    else if (value instanceof Date) d = value;
    if (!d || Number.isNaN(d.getTime())) return null;
    return d.getTime();
  } catch {
    return null;
  }
}

/**
 * Builds the activity trail for an intake that is out with the client.
 * Degrades to a single "sent" event when there are no submissions yet.
 */
export function buildAwaitingForeignNationalTimeline(opts: {
  intake: OutgoingIntake;
  submissions: OutgoingIntakeSubmission[];
  attorneyName: string;
}): IntakeTimelineEvent[] {
  const { intake, submissions } = opts;
  const events: IntakeTimelineEvent[] = [];
  const attorneyName = safeString(opts.attorneyName).trim() || "You";

  const sentAt = intake.sent_at ?? intake.created_at ?? null;
  if (sentAt) {
    events.push({
      id: "sent",
      kind: "sent",
      label: `${attorneyName} sent the intake`,
      at: sentAt,
      actor: "attorney"
    });
  }

  const normalizedSubs = (submissions ?? []).filter(Boolean);

  // First open event (best available signal).
  const earliestCreatedAt =
    normalizedSubs
      .map((s) => s.created_at ?? null)
      .filter((v) => toMillis(v) !== null)
      .sort((a, b) => (toMillis(a) ?? 0) - (toMillis(b) ?? 0))[0] ?? null;

  if (earliestCreatedAt) {
    events.push({
      id: "opened",
      kind: "opened",
      label: "Client opened link",
      at: earliestCreatedAt,
      actor: "client"
    });
  } else if (safeString(intake.status ?? "").trim() === "in_progress") {
    // Fallback: in progress but we don't have submission metadata yet.
    const openedAt = intake.updated_at ?? intake.sent_at ?? null;
    if (openedAt) {
      events.push({
        id: "opened",
        kind: "opened",
        label: "Client opened link",
        at: openedAt,
        actor: "client"
      });
    }
  }

  // Submitted events (can happen multiple times across revisions).
  normalizedSubs.forEach((s) => {
    const baseId = safeString(s.outgoing_intake_submission_id ?? "").trim();
    const submittedAt = s.submitted_at ?? null;
    if (!submittedAt) return;
    events.push({
      id: `${baseId || "submission"}::submitted`,
      kind: "submitted",
      label: "Client submitted",
      at: submittedAt,
      actor: "client"
    });
  });

  // Revision requested events (client needs to act again).
  normalizedSubs.forEach((s) => {
    if (safeString(s.review_status ?? "").trim() !== "revision_requested")
      return;
    const baseId = safeString(s.outgoing_intake_submission_id ?? "").trim();
    const at = s.reviewed_at ?? s.updated_at ?? null;
    if (!at) return;
    events.push({
      id: `${baseId || "submission"}::revision_requested`,
      kind: "revision_requested",
      label: `${attorneyName} requested a revision`,
      at,
      actor: "attorney"
    });
  });

  return events
    .filter((e) => toMillis(e.at) !== null)
    .sort((a, b) => (toMillis(a.at) ?? 0) - (toMillis(b.at) ?? 0));
}
